import type { StunTestModalProps } from './index.types';
import React, { useCallback, useState } from 'react';
import {
  comprehensiveStunTest,
  formatStunTestResults,
} from 'Utils/diagnostics/stunTest';
import { getLogger } from 'Utils/logger';
import { DiagnosticsModal } from '../DiagnosticsModal';

export const StunTestModal: React.FC<StunTestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<string | null>(null);

  const runTest = useCallback(async () => {
    setIsRunning(true);
    setResults(null);

    try {
      const testResults = await comprehensiveStunTest();
      const formattedResults = formatStunTestResults(testResults);
      setResults(formattedResults);

      // Send results to log collector
      const logger = getLogger();
      logger.info('STUN Test Completed', {
        testResults,
        formattedResults,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      });
    } catch (error) {
      const errorMessage = `Error running STUN test: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      setResults(errorMessage);

      // Log error
      const logger = getLogger();
      logger.error('STUN Test Failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });
    } finally {
      setIsRunning(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    setResults(null);
    onClose();
  }, [onClose]);

  return (
    <DiagnosticsModal
      title={'Running STUN Test'}
      description={
        'This test checks if STUN (Session Traversal Utilities for NAT) can discover your public IP address. STUN is essential for WebRTC calls to work behind NAT/firewalls.'
      }
      placeholder={
        <div>
          <strong>What this test does:</strong>
          <ul>
            <li>Tests connectivity to multiple STUN servers</li>
            <li>Discovers your public IP address</li>
            <li>Verifies NAT traversal capability</li>
            <li>Sends results to support team automatically</li>
          </ul>
        </div>
      }
      isOpen={isOpen}
      isRunning={isRunning}
      results={results}
      runTest={() => void runTest()}
      onClose={handleClose}
    />
  );
};
